import {
	PriceTotalKey,
	type sectionMapConfig,
} from './GoodsAndServicesMapConfig'
import {
	gMerchandiseMapConfig,
	gServiceCheckBoxMapConfig,
	gServiceMapSubItemsConfig,
	ngCashAdvanceCheckBoxMapConfig,
	ngCashAdvanceMapConfig,
	priceTotalMapConfig,
} from './TexasGoodsAndServicesMapConfig'
import {
	GAUGE_VALUE,
	INTERIOR_LINING_VALUE,
	type MappedCustomItem,
	MATERIAL_VALUE,
	type MerchandiseLineItem,
	OUNCE_VALUE,
	OUTER_BURIAL_CONTAINER_TYPE,
	Pb,
	SEAL_VALUE,
	SHELL_VALUE,
} from './formMappingTypes'
import GoodsAndServicesForm from '@/components/documents/Foxit/FormMapping/GoodsAndServicesForm'
import { PbProductType } from '@/enums/PbProductType'
import type {
	CartItemAndLineItem,
	CartSubItemAndLineItem,
} from '@/mixins/merchandise'
import { Frequency, type ScenarioOutput, Term } from '@precoa/calculator-sdk'

// Cash advance 'Other' items share the four ngOtherOtherLine lines
const cashAdvanceOtherLineKeys = [
	'CashAdvance-Other',
	'Other-Other',
	'Other-Other Line 2',
]

export default class TexasGoodsAndServicesForm extends GoodsAndServicesForm {
	private paymentDetails: ScenarioOutput | undefined
	protected readonly hasSupplementalPage = false
	protected readonly sharesEmbalmingAndRefrigerationLine = false

	constructor(
		planHolderId: string,
		cartItems?: CartItemAndLineItem[],
		formatPrice?: (price: number | null | undefined) => '' | string,
		translationFunction?: ((key: string) => string) | undefined,
		activePriceBookVersion?: number,
		paymentDetails?: ScenarioOutput
	) {
		super(
			planHolderId,
			cartItems,
			formatPrice,
			translationFunction,
			activePriceBookVersion,
			paymentDetails
		)

		this.paymentDetails = paymentDetails
	}

	protected generateFieldMappingsFromSectionConfig(
		sectionConfig: sectionMapConfig,
		sectionItemsMap: Map<string, any[]>
	) {
		return sectionConfig.map(({ name, category, prop }) => {
			let mapItem = sectionItemsMap.get(category)
			let itemValue: string | number = ''

			if (!mapItem) {
				return {
					name,
					value: '',
				}
			}

			if (mapItem.length > 1) {
				let commaSeparatedItems: string[] = []

				mapItem.forEach((item) => {
					const itemShortDescription = item[prop]
						? `${item[prop]}`
						: item.shortDescription
							? `${item.shortDescription}`
							: ''

					commaSeparatedItems.push(itemShortDescription)
				})

				// Skip items without a description so the line doesn't start or end with a comma
				const commaSeparatedItemsString = commaSeparatedItems
					.filter(Boolean)
					.join(', ')

				itemValue =
					prop === 'price'
						? this.fieldPrices[category] === 0
							? 'Incl'
							: (this.fieldPrices[category].toFixed(2) ?? '')
						: commaSeparatedItemsString
			} else {
				itemValue = this.setItemValueForSectionItems(
					category,
					sectionItemsMap,
					prop,
					false
				)
			}

			return {
				name,
				value: itemValue ?? '',
			}
		})
	}

	protected getServicesMapConfig = () => {
		return gServiceMapSubItemsConfig
	}

	protected getMerchandiseMapConfig = () => {
		return gMerchandiseMapConfig
	}

	protected getCashAdvanceMapConfig = () => {
		return ngCashAdvanceMapConfig
	}

	protected getServicesCheckBoxMapConfig = () => {
		return gServiceCheckBoxMapConfig
	}

	protected getCashAdvanceCheckBoxMapConfig = () => {
		return ngCashAdvanceCheckBoxMapConfig
	}

	// Returns form filling array for Section A (Guaranteed Services) non-custom line items
	protected getServiceSubItemMappings() {
		const config = this.getServicesMapConfig()
		const servicesFields = this.generateFieldMappingsFromSectionConfig(
			config,
			this.servicesMap
		)

		return [
			...servicesFields,
			...this.generateCheckBoxMappingsFromConfig(
				this.getServicesCheckBoxMapConfig(),
				this.servicesMap
			),
		]
	}

	// Returns form filling array for Section B (Guaranteed Merchandise) non-custom line items
	protected getMerchandiseMappings() {
		const config = this.getMerchandiseMapConfig()
		const merchandiseFields = this.generateFieldMappingsFromSectionConfig(
			config,
			this.merchandiseMap
		)

		return [...merchandiseFields]
	}

	// Returns form filling array for Section C (Non-Guaranteed CashAdvance) non-custom line items
	protected getCashAdvancedMappings = () => {
		const config = this.getCashAdvanceMapConfig()
		const cashAdvanceFields = this.generateFieldMappingsFromSectionConfig(
			config,
			this.cashAdvanceMap
		)

		return [
			...cashAdvanceFields,
			...this.generateCheckBoxMappingsFromConfig(
				this.getCashAdvanceCheckBoxMapConfig(),
				this.cashAdvanceMap
			),
		]
	}

	private getPaymentTermAndModeMappings() {
		const paymentTermLength = this.planHolder.insurance.term
		let termLength = ''

		switch (paymentTermLength) {
			case Term.ThreeYear:
				termLength = '3'
				break
			case Term.FiveYear:
				termLength = '5'
				break
			case Term.TenYear:
				termLength = '10'
				break
			case Term.TwentyYear:
				termLength = '20'
				break
			default:
				termLength = ''
		}

		const frequency = this.planHolder.insurance.paymentFrequency

		return [
			{
				name: 'paymentPlanYears',
				value: termLength,
			},
			this.getCheckBoxMapping(
				'paymentSinglePayCheck',
				frequency === Frequency.OneTime
			),
			this.getCheckBoxMapping(
				'paymentModeMonthlyDirectCheck',
				frequency === Frequency.Monthly
			),
			this.getCheckBoxMapping(
				'paymentModeQuarterlyCheck',
				frequency === Frequency.Quarterly
			),
			this.getCheckBoxMapping(
				'paymentModeSemiAnnualCheck',
				frequency === Frequency.SemiAnnual
			),
			this.getCheckBoxMapping(
				'paymentModeAnnualCheck',
				frequency === Frequency.Annual
			),
		]
	}

	// Helper to gather singlePay/multiPay amount for Section 5's Face and Payment Information section
	private getPaymentAmountMappings() {
		if (!this.paymentDetails) return []

		return [
			{
				name: 'totalPremiumAmt',
				value: this.formatAmount(this.paymentDetails.totalPaidAmt),
			},
			{
				name: 'singlePayFaceAmount',
				value: this.formatAmount(this.paymentDetails.singlePayFaceAmt),
			},
			{
				name: 'singlePayPaymentToday',
				value: this.formatAmount(this.paymentDetails.singlePayDueTodayAmt),
			},
			{
				name: 'multiPayFaceAmount',
				value: this.formatAmount(this.paymentDetails.multiPayFaceAmt),
			},
			{
				name: 'multiPayMonthlyPayment',
				value: this.formatAmount(this.paymentDetails.multiPayPeriodicAmt),
			},
			{
				name: 'multiPayPaymentToday',
				value: this.formatAmount(this.paymentDetails.multiPayDueTodayAmt),
			},
			{
				name: 'totalFaceAmount',
				value: this.formatAmount(this.paymentDetails.totalFaceAmt),
			},
			{
				name: 'totalFirstPayment',
				value: this.formatAmount(this.paymentDetails.totalDueTodayAmt),
			},
		]
	}

	protected setMappedMerchandise(
		key: string,
		isIncludedInPkg: boolean,
		cartItem?: CartItemAndLineItem | CartSubItemAndLineItem,
		customItem?: MappedCustomItem
	) {
		let line
		let item

		if (cartItem) {
			line = cartItem.lineItem as MerchandiseLineItem

			if (!line) {
				return
			}

			const isPackageIncludedItem = !this.packageIsBundle && isIncludedInPkg

			item = {
				...line,
				name: line?.name,
				category: line?.category ?? '',
				subcategory: line?.subcategory ?? '',
				interiorDescription: line?.interiorDescription ?? '',
				exteriorDescription: line?.exteriorDescription ?? '',
				modelNameNumber: line?.modelNameNumber ?? '',
				productManufacturer: line?.productManufacturer ?? '',
				isGuaranteed: line?.isGuaranteed,
				price: this.getItemPriceByPriceBookVersion(
					cartItem,
					line,
					isPackageIncludedItem,
					'merchandise'
				),
				quantity: cartItem.quantity ?? 1,
				shortDescription:
					(line?.shortDescription ?? line?.subcategory === Pb.Discount)
						? line.name
						: '',
			}
		}

		if (customItem) {
			item = customItem
		}

		if (key.includes(Pb.Other)) {
			key = this.getOtherLineKey(item)
		}

		let merchandiseSubCatArray = this.merchandiseMap.get(key)

		if (!merchandiseSubCatArray) {
			this.merchandiseMap.set(key, [])
			merchandiseSubCatArray = []
		}

		this.merchandiseMap.set(key, [...merchandiseSubCatArray, item])
	}

	protected getOtherLineKey(item: any): string {
		const key = item.subcategory

		if (item.category === PbProductType.Package) {
			return `${PbProductType.Transportation}-${Pb.Other2}`
		}

		if (key === Pb.Other || key === Pb.Other2) {
			return `${item.category}-${item.subcategory}`
		}

		return key
	}

	private getDiscountsMappings() {
		const discountValues = this.getDiscountValues()

		if (!discountValues.description && !discountValues.total) {
			return []
		}

		return [
			{
				name: 'discountsDesc',
				value: discountValues.description,
			},
			{
				name: 'discountsAmt',
				value: discountValues.total.toFixed(2),
			},
		]
	}

	// Helper to return the total of the accumulated discounts from the services, merchandise, and cash advance sections
	private getDiscountValues(): { description: string; total: number } {
		const serviceDiscounts = this.servicesMap.get('Discount') ?? []
		const merchandiseDiscounts = this.merchandiseMap.get('Discount') ?? []
		const cashAdvanceDiscounts = this.cashAdvanceMap.get('Discount') ?? []

		const totalDiscounts = [
			...serviceDiscounts,
			...merchandiseDiscounts,
			...cashAdvanceDiscounts,
		]

		if (totalDiscounts.length === 0) {
			return { description: '', total: 0 }
		}

		const normalizedTotalDiscounts = totalDiscounts.map((item) => {
			return {
				price: item.price === 'Incl' ? 0 : item.price,
				shortDescription: item.shortDescription,
			}
		})

		return {
			description:
				normalizedTotalDiscounts.length > 1
					? 'Total Discounts'
					: normalizedTotalDiscounts[0].shortDescription,
			total: Math.abs(
				normalizedTotalDiscounts.reduce((acc, item) => acc + item.price, 0)
			),
		}
	}

	private getCashAdvanceOtherMappings() {
		const result: { name: string; value: string | number }[] = []
		const otherItems = cashAdvanceOtherLineKeys.flatMap(
			(key) => this.cashAdvanceMap.get(key) ?? []
		)

		if (otherItems.length > 0) {
			otherItems.slice(0, 3).forEach((item, index) => {
				result.push(
					{
						name: `ngOtherOtherLine${index + 1}Desc`,
						value: item.shortDescription,
					},
					{
						name: `ngOtherOtherLine${index + 1}Amt`,
						value: item.price.toFixed(2),
					},
					{
						name: `ngOtherOtherLine${index + 1}Check`,
						value: 'Yes',
					}
				)
			})

			const line4Items = otherItems.slice(3)

			if (line4Items.length > 0) {
				const line4Breakdown = line4Items.reduce(
					(acc, item, index) => {
						return {
							price: acc.price + item.price,
							shortDescription: [
								...acc.shortDescription,
								item.shortDescription,
							],
						}
					},
					{ price: 0, shortDescription: [] }
				)

				result.push(
					{
						name: 'ngOtherOtherLine4Amt',
						value: line4Breakdown.price.toFixed(2),
					},
					{
						name: 'ngOtherOtherLine4Desc',
						value: line4Breakdown.shortDescription.join(', '),
					},
					{
						name: 'ngOtherOtherLine4Check',
						value: 'Yes',
					}
				)
			}
		}

		return result
	}

	protected getCustomItemSharedLineFieldNames(key: string): string[] {
		if (key === Pb.Discount) {
			return ['discountsDesc', 'discountsAmt']
		}

		if (cashAdvanceOtherLineKeys.includes(key)) {
			return [1, 2, 3, 4].flatMap((line) => [
				`ngOtherOtherLine${line}Desc`,
				`ngOtherOtherLine${line}Amt`,
				`ngOtherOtherLine${line}Check`,
			])
		}

		if (key === Pb.MerchandiseCasket) {
			return this.getCasketMappings().map(({ name }) => name)
		}

		if (key === Pb.MerchandiseOuterBurialContainer) {
			return this.getBurialContainerMappings().map(({ name }) => name)
		}

		return super.getCustomItemSharedLineFieldNames(key)
	}

	private getBurialContainerMappings() {
		//Get burial container from merchandise map
		const container = this.merchandiseMap.get(
			Pb.MerchandiseOuterBurialContainer
		)?.[0]

		return [
			...this.getOuterBurialMappings(container),
			...this.getOuterBurialBronzeMapping(container),
			...this.getOuterBurialCopperMapping(container),
			...this.getOuterBurialSteelMapping(container),
			...this.getOuterBurialConcreteMapping(container),
			...this.getOuterBurialWoodMapping(container),
			...this.getOuterBurialTypeMapping(container),
			...this.getOuterBurialSealantMapping(container),
		]
	}

	private getOuterBurialConcreteMapping(container: any) {
		return [
			{
				name: 'gOuterBurialConcreteCheck',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.CONCRETE
						? 'Yes'
						: 'Off',
			},
		]
	}

	private getOuterBurialWoodMapping(container: any) {
		return [
			{
				name: 'gOuterBurialWoodCheck',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.WOOD
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialWoodTypeDesc',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.WOOD
						? container?.productTypeSubtypeOther
						: '',
			},
		]
	}

	private getOuterBurialBronzeMapping(container: any) {
		return [
			{
				name: 'gOuterBurialBronzeOzDesc',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.BRONZE
						? container.ouncesOz
						: '',
			},
		]
	}

	private getOuterBurialCopperMapping(container: any) {
		return [
			{
				name: 'gOuterBurialCopperOzDesc',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.COPPER
						? container.ouncesOz
						: '',
			},
		]
	}

	private getOuterBurialSteelMapping(container: any) {
		return [
			{
				name: 'gOuterBurialSteel7Check',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					container?.subtype?.toLowerCase() === GAUGE_VALUE.SEVEN_GAUGE
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialSteel10Check',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					container?.subtype?.toLowerCase() === GAUGE_VALUE.TEN_GAUGE
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialSteel12Check',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					container?.subtype?.toLowerCase() === GAUGE_VALUE.TWELVE_GAUGE
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialSteel14Check',
				value:
					container?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					container?.subtype?.toLowerCase() === GAUGE_VALUE.FOURTEEN_GAUGE
						? 'Yes'
						: 'Off',
			},
		]
	}

	private getOuterBurialSealantMapping(container: any) {
		return [
			{
				name: 'gOuterBurialSealCheck',
				value:
					container?.sealantOption?.toLowerCase() === SEAL_VALUE.SEAL
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialNonSealCheck',
				value:
					container?.sealantOption?.toLowerCase() === SEAL_VALUE.NON_SEAL
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialNASealCheck',
				value:
					container?.sealantOption?.toLowerCase() === SEAL_VALUE.NOT_APPLICABLE
						? 'Yes'
						: 'Off',
			},
		]
	}

	private getOuterBurialMappings(container: any) {
		return [
			{
				name: 'gOuterBurialModelDesc',
				value: container?.name ?? '',
			},
			{
				name: 'gOuterBurialAmt',
				value:
					typeof container?.price === 'number'
						? container?.price.toFixed(2)
						: container?.price,
			},
		]
	}

	private getOuterBurialTypeMapping(container: any) {
		return [
			{
				name: 'gOuterBurialLinerCheck',
				value:
					container?.outerBurialContainerType?.toLowerCase() ===
					OUTER_BURIAL_CONTAINER_TYPE.LINER
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialVaultCheck',
				value:
					container?.outerBurialContainerType?.toLowerCase() ===
					OUTER_BURIAL_CONTAINER_TYPE.VAULT
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialBoxCheck',
				value:
					container?.outerBurialContainerType?.toLowerCase() ===
					OUTER_BURIAL_CONTAINER_TYPE.BOX
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialOtherCheck',
				value:
					container?.outerBurialContainerType?.toLowerCase() ===
					OUTER_BURIAL_CONTAINER_TYPE.OTHER
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gOuterBurialOtherDesc',
				value:
					container?.outerBurialContainerType?.toLowerCase() ===
					OUTER_BURIAL_CONTAINER_TYPE.OTHER
						? container?.productTypeSubtypeOther
						: '',
			},
		]
	}

	private getCasketMappings() {
		const casket = this.merchandiseMap.get(Pb.MerchandiseCasket)?.[0]

		return [
			...this.getCasketBaseMappings(casket),
			...this.getWoodMapping(casket),
			...this.getCopperMapping(casket),
			...this.getSteelMapping(casket),
			...this.getOtherTypeMapping(casket),
			...this.getBronzeMapping(casket),
			...this.getShellMapping(casket),
			...this.getInteriorLiningMappings(casket),
			...this.getSealantMapping(casket),
			...this.getExteriorMapping(casket),
		]
	}

	private getCasketBaseMappings(casket: any) {
		const casketPrice = casket?.price ?? casket?.fixedPrice
		return [
			{
				name: 'gCasketManufacturerDesc',
				value: casket?.productManufacturer ?? '',
			},
			{
				name: 'gCasketModelDesc',
				value: casket?.modelNameNumber ?? '',
			},
			{
				name: 'gCasketAmt',
				value:
					typeof casketPrice === 'number'
						? casketPrice.toFixed(2)
						: casketPrice,
			},
		]
	}

	private getSealantMapping(casket: any) {
		return [
			{
				name: 'gCasketSealCheck',
				value:
					casket?.sealantOption?.toLowerCase() === SEAL_VALUE.SEAL
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketNonSealCheck',
				value:
					casket?.sealantOption?.toLowerCase() === SEAL_VALUE.NON_SEAL
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketNASealCheck',
				value:
					casket?.sealantOption?.toLowerCase() === SEAL_VALUE.NOT_APPLICABLE
						? 'Yes'
						: 'Off',
			},
		]
	}

	private getShellMapping(casket: any) {
		return [
			{
				name: 'gCasketShellSquareCheck',
				value:
					casket?.shell?.toLowerCase() === SHELL_VALUE.SQUARE ? 'Yes' : 'Off',
			},
			{
				name: 'gCasketShellRoundCheck',
				value:
					casket?.shell?.toLowerCase() === SHELL_VALUE.ROUND ? 'Yes' : 'Off',
			},
		]
	}

	private getInteriorLiningMappings(casket: any) {
		return [
			{
				name: 'gCasketInteriorCrepeCheck',
				value:
					casket?.interiorLining?.toLowerCase() === INTERIOR_LINING_VALUE.CREPE
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketInteriorVelvetCheck',
				value:
					casket?.interiorLining?.toLowerCase() === INTERIOR_LINING_VALUE.VELVET
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketInteriorSatinCheck',
				value:
					casket?.interiorLining?.toLowerCase() === INTERIOR_LINING_VALUE.SATIN
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketInteriorOtherCheck',
				value:
					casket?.interiorLining?.toLowerCase() === MATERIAL_VALUE.OTHER
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketInteriorOtherDesc',
				value:
					casket?.interiorLining?.toLowerCase() === MATERIAL_VALUE.OTHER
						? casket?.shortDescription
						: '',
			},
		]
	}

	private getOtherTypeMapping(casket: any) {
		return [
			{
				name: 'gCasketOtherExteriorDesc',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.OTHER
						? casket.productTypeSubtypeOther
						: '',
			},
		]
	}

	private getWoodMapping(casket: any) {
		return [
			{
				name: 'gCasketWoodDescription',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.WOOD &&
					casket?.subtype?.toLowerCase() === MATERIAL_VALUE.OTHER
						? casket.productTypeSubtypeOther
						: '',
			},
		]
	}

	private getBronzeMapping(casket: any) {
		return [
			{
				name: 'gCasketBronze32Check',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.BRONZE &&
					casket?.subtype?.toLowerCase() === OUNCE_VALUE.THIRTY_TWO_OZ
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketBronze48Check',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.BRONZE &&
					casket?.subtype?.toLowerCase() === OUNCE_VALUE.FORTY_EIGHT_OZ
						? 'Yes'
						: 'Off',
			},
		]
	}

	private getSteelMapping(casket: any) {
		return [
			{
				name: 'gCasketSteel16Check',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					casket?.subtype?.toLowerCase() === GAUGE_VALUE.SIXTEEN_GAUGE
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketSteel18Check',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					casket?.subtype?.toLowerCase() === GAUGE_VALUE.EIGHTEEN_GAUGE
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketSteel20Check',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					casket?.subtype?.toLowerCase() === GAUGE_VALUE.TWENTY_GAUGE
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketSteelOtherDesc',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					casket?.subtype?.toLowerCase() === MATERIAL_VALUE.OTHER
						? casket.productTypeSubtypeOther
						: '',
			},
			{
				name: 'gCasketSteelOtherCheck',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					casket?.subtype?.toLowerCase() === MATERIAL_VALUE.OTHER
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketSteelStainlessCheck',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.STEEL &&
					casket?.subtype?.toLowerCase() === MATERIAL_VALUE.STAINLESS
						? 'Yes'
						: 'Off',
			},
		]
	}

	private getCopperMapping(casket: any) {
		return [
			{
				name: 'gCasketCopper32Check',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.COPPER &&
					casket?.subtype?.toLowerCase() === OUNCE_VALUE.THIRTY_TWO_OZ
						? 'Yes'
						: 'Off',
			},
			{
				name: 'gCasketCopper48Check',
				value:
					casket?.type?.toLowerCase() === MATERIAL_VALUE.COPPER &&
					casket?.subtype?.toLowerCase() === OUNCE_VALUE.FORTY_EIGHT_OZ
						? 'Yes'
						: 'Off',
			},
		]
	}

	private getExteriorMapping(casket: any) {
		return [
			{
				name: 'gCasketExteriorColorDesc',
				value: casket?.exteriorColor ?? '',
			},
		]
	}

	protected calculateSectionTotals() {
		this.fieldPrices[PriceTotalKey.GoodsAndServicesTotal] =
			(this.fieldPrices[PriceTotalKey.ServicesTotal] ?? 0) +
			(this.fieldPrices[PriceTotalKey.MerchandiseTotal] ?? 0)

		this.fieldPrices[PriceTotalKey.FuneralSubtotal] =
			this.fieldPrices[PriceTotalKey.GoodsAndServicesTotal] +
			(this.fieldPrices[PriceTotalKey.CashAdvanceTotal] ?? 0)

		this.fieldPrices[PriceTotalKey.FuneralTotal] =
			this.fieldPrices[PriceTotalKey.GoodsAndServicesTotal] +
			(this.fieldPrices[PriceTotalKey.CashAdvanceTotal] ?? 0) -
			(Number(this.getDiscountValues().total) ?? 0)
	}

	protected getPriceTotalFieldsMappings() {
		const priceTotalFields = [
			...priceTotalMapConfig.map(({ name, key }) => ({
				name,
				value: this.fieldPrices[key].toFixed(2) ?? '',
			})),
		]

		return priceTotalFields
	}

	getFormMappings() {
		this.extractCleanedLineItems()
		this.getFieldPricesBreakdown(false)

		return [
			// entities mappings
			...this.getProposedInsuredMapping(),
			...this.getProposedInsuredAddressMapping(),
			...this.getFuneralHomeRepresentativeMappings(),
			...this.getPolicyOwnerMapping(),
			...this.getPolicyOwnerAddressMapping(),
			...this.getFuneralHomeMapping(),
			...this.getDiscountsMappings(),
			...this.getPriceTotalFieldsMappings(),
			// state specific mappings
			...this.getServiceSubItemMappings(),
			...this.getMerchandiseMappings(),
			...this.getCashAdvancedMappings(),

			...this.getPaymentTermAndModeMappings(),
			...this.getPaymentAmountMappings(),
			...this.getCashAdvanceOtherMappings(),
			...this.getCasketMappings(),
			...this.getBurialContainerMappings(),
		]
	}
}
